/** The combat state: reacting, holding, advancing, flanking, and fighting from cover. */

import { bark } from '../alert';
import type { Ctx } from '../ctx';
import { atan2 } from '../math';
import { byThreat, ENEMY, secs, TACTICS, THREAT } from '../tuning';
import type { Enemy } from '../types';
import { travel, turnToward } from './movement';
import { maybeThrowGrenade, shootAtRanger } from './shooting';
import { chooseRole, officerNear } from './tactics';

export function enterCombat(ctx: Ctx, e: Enemy): void {
  if (e.state === 'combat' || e.state === 'runner' || e.state === 'flee' || e.state === 'rescue') return;
  e.state = 'combat';
  e.burstLeft = 0;
  e.fireCooldown = 0;
  const base = byThreat(ctx.statics.def.threat, THREAT.reactionSeconds[0], THREAT.reactionSeconds[1]);
  const temper = e.persona === 'aggressive' ? 0.7 : e.persona === 'jumpy' ? 1.4 : 1;
  e.reactTicks = secs(base * temper * (officerNear(ctx.world, e) ? 0.7 : 1));
  e.path = [];
  e.tacticTicks = secs(0.5);
  ctx.events.push({ type: 'spotted', id: e.id });
  // He shouts: men close by come running, but a shout is not an alarm.
  bark(ctx, e, 'contact');
}

export function searchSeconds(ctx: Ctx): number {
  return byThreat(ctx.statics.def.threat, THREAT.searchSeconds[0], THREAT.searchSeconds[1]);
}

export function stepCombat(ctx: Ctx, e: Enemy): void {
  const w = ctx.world;
  const r = w.ranger;
  const dx = r.x - e.x;
  const dz = r.z - e.z;
  const dist = Math.sqrt(dx * dx + dz * dz);

  e.tacticTicks--;
  if (e.tacticTicks <= 0) {
    chooseRole(ctx, e);
    if (e.state !== 'combat') return;
  }
  if (e.reactTicks > 0) {
    e.reactTicks--;
    e.moving = false;
    if (e.seesRanger) turnToward(e, atan2(dx, dz), 9);
    return;
  }

  // A ranger who is down and has stopped shooting is a prisoner, not a target: men close in
  // to take him rather than finish him. This is what makes capture - which a rescue mission
  // can undo - the usual end of a lost fight, and death the exception (04 §3, §12).
  if (r.status === 'downed' && r.sinceShot > TACTICS.holdFireAfterTicks) {
    e.role = 'hold';
    e.peeking = false;
    if (dist > 3) travel(ctx, e, r.x, r.z, ENEMY.walkSpeed * 1.5, 2.5);
    else {
      e.moving = false;
      turnToward(e, atan2(dx, dz), 9);
    }
    return;
  }

  if (!e.seesRanger && e.lostTicks > ENEMY.lostSightTicks) {
    if (maybeThrowGrenade(ctx, e)) return;
    if (e.role === 'cover' && e.hasSpot && e.lostTicks < secs(4)) {
      // Sit tight behind cover for a moment rather than walking into his sights.
      if (travel(ctx, e, e.spotX, e.spotZ, ENEMY.huntSpeed, 0.7)) turnToward(e, atan2(e.targetX - e.x, e.targetZ - e.z), 6);
      return;
    }
    // Go to where he was last seen, then search: fixes the original's forgetful AI.
    if (travel(ctx, e, e.targetX, e.targetZ, ENEMY.huntSpeed)) {
      e.state = 'search';
      e.stateTicks = secs(searchSeconds(ctx));
      e.path = [];
      bark(ctx, e, 'searching');
    }
    return;
  }

  const preferred = e.persona === 'aggressive' ? 9 : e.persona === 'cautious' ? 17 : ENEMY.preferredRange;
  switch (e.role) {
    case 'flank': {
      const arrived = !e.hasSpot || travel(ctx, e, e.spotX, e.spotZ, ENEMY.huntSpeed, 1.5);
      // Breaks off the manoeuvre the moment he has a close shot.
      if (arrived || (e.seesRanger && dist < 11)) {
        e.role = 'hold';
        e.hasSpot = false;
        e.path = [];
      }
      break;
    }
    case 'cover': {
      if (!e.hasSpot) {
        e.role = 'hold';
        break;
      }
      const ready = e.fireCooldown <= 0 && e.suppressTicks <= 0;
      if (e.peeking) {
        if (e.seesRanger) {
          e.moving = false;
          turnToward(e, atan2(dx, dz), 9);
          shootAtRanger(ctx, e);
          if (e.burstLeft <= 0) e.peeking = false;
        } else {
          // Step sideways out of cover until the target is in view.
          const side = ((e.id & 1) === 0 ? 1 : -1) * 1.6;
          const l = Math.max(0.01, dist);
          travel(ctx, e, e.spotX + (-dz / l) * side, e.spotZ + (dx / l) * side, ENEMY.walkSpeed * 1.4, 0.4);
          if (e.suppressTicks > 0) e.peeking = false;
        }
      } else if (travel(ctx, e, e.spotX, e.spotZ, ENEMY.huntSpeed, 0.6)) {
        turnToward(e, atan2(dx, dz), 6);
        if (ready) {
          e.peeking = true;
          e.burstLeft = 0;
          e.path = [];
        }
      }
      break;
    }
    case 'advance':
    case 'hold': {
      if (e.seesRanger) {
        turnToward(e, atan2(dx, dz), 9);
        if (dist > preferred && e.burstLeft <= 0 && e.suppressTicks <= 0) travel(ctx, e, r.x, r.z, ENEMY.walkSpeed * (e.role === 'advance' ? 1.8 : 1.3));
        else {
          e.moving = false;
          shootAtRanger(ctx, e);
        }
      }
      break;
    }
  }
}
